<template>
  <div class="modal-backdrop">
    <VueGlobalEvents @keyup.esc="onClose" />
    <VueFocusLock>
      <div class="modal-content">
        <button v-focus type="button" class="close" @click="onClose">
          <VSvgIcon name="cancel" variant="outlined" size="large" />
          <span class="sr-only">{{ $t('common.close') }}</span>
        </button>
        <template v-if="errorCode">
          <div class="modal-header">
            <h2 class="h4 modal-title">{{ $t('common.headerError') }}</h2>
          </div>
          <div class="modal-body">
            <p>{{ $t('common.' + errorCode) }}</p>
            <p v-if="$te(errorCodeMessage)">{{ $t(errorCodeMessage) }}</p>
          </div>
        </template>
        <div v-else-if="loading" class="modal-body offset-left-3 offset-right-3">
          <transition name="fade" mode="out-in" appear>
            <VSpinner class="load-spin" />
          </transition>
        </div>
        <template v-else>
          <transition name="fade" mode="out-in" appear>
            <ModalInfo v-if="isPartner" :image-path="`delegates/barring/content/businesspartner/${partnerId}`" :has-image="hasImage">
              <template #headline>
                {{ modalInfo.name }}
              </template>

              <template #default>
                <dl>
                  <template v-if="modalInfo.website">
                    <dt>{{ $t('common.website') }}:</dt>
                    <dd>{{ modalInfo.website }}</dd>
                  </template>
                  <template v-if="modalInfo.email">
                    <dt>{{ $t('common.email') }}:</dt>
                    <dd>{{ modalInfo.email }}</dd>
                  </template>
                  <template v-if="modalInfo.phone">
                    <dt>{{ $t('common.phone') }}:</dt>
                    <dd>{{ modalInfo.phone }}</dd>
                  </template>
                  <template v-if="modalInfo.businessPartnerInformation">
                    <dt>{{ $t('common.address') }}:</dt>
                    <dd v-if="modalInfo.businessPartnerInformation.street && modalInfo.businessPartnerInformation.streetNumber">
                      {{ modalInfo.businessPartnerInformation.street + ' ' + modalInfo.businessPartnerInformation.streetNumber }}</dd
                    >
                    <dd v-if="modalInfo.businessPartnerInformation.zipCode && modalInfo.businessPartnerInformation.city">
                      {{ modalInfo.businessPartnerInformation.zipCode + ' ' + modalInfo.businessPartnerInformation.city }}</dd
                    >
                  </template>
                  <template v-else-if="modalInfo.factoringInformation">
                    <dt>{{ $t('common.address') }}:</dt>
                    <dd v-if="modalInfo.factoringInformation.address1">{{ modalInfo.factoringInformation.address1 }}</dd>
                    <dd v-if="modalInfo.factoringInformation.address2">{{ modalInfo.factoringInformation.address2 }}</dd>
                    <dd v-if="modalInfo.factoringInformation.address3">{{ modalInfo.factoringInformation.address3 }}</dd>
                    <dd v-if="modalInfo.factoringInformation.address4">{{ modalInfo.factoringInformation.address4 }}</dd>
                  </template>
                </dl>
              </template>
            </ModalInfo>
            <ModalInfo v-else :image-path="`delegates/barring/content/categories/${getName(partnerId)}`" :has-image="hasImage">
              <template #headline>
                {{ modalInfo.contentTypeName || modalInfo.contentTypeCode }}
              </template>
              <template #default>
                <div class="category">
                  <div v-if="modalInfo.contentTypeDescription" class="description">{{ modalInfo.contentTypeDescription }}</div>
                </div>
              </template>
            </ModalInfo>
          </transition>
        </template>
      </div>
    </VueFocusLock>
  </div>
</template>

<script>
import VueFocusLock from 'vue-focus-lock';
import VSpinner from '@/components/Spinner';
import focus from '@/directives/focus';
import Proxy from '@/proxies/BarringProxy';
import ModalInfo from '@/components/Barring/Info/ModalInfo';
import VSvgIcon from '@/components/UI/VSvgIcon.vue';

export default {
  name: 'Modalinfo',

  directives: {
    focus,
  },

  components: {
    VSvgIcon,
    VSpinner,
    VueGlobalEvents: () => import('vue-global-events'),
    VueFocusLock,
    ModalInfo,
  },

  props: {
    partnerId: {
      type: String,
      required: true,
    },

    hasImage: {
      type: Boolean,
      required: true,
    },

    type: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      modalInfo: null,
      errorCode: null,
      errorCodeMessage: null,
      loading: true,
      isPartner: true,
    };
  },

  async created() {
    this.previousElement = document.activeElement;
    try {
      if (this.type === 'category') {
        this.isPartner = false;
        if (this.partnerId === 'VoicePartners') {
          this.modalInfo = this.getVoicePartnerDescription();
        } else {
          this.modalInfo = await new Proxy().getCategory(this.partnerId);
        }
      } else {
        this.modalInfo = await new Proxy().getPartner(this.partnerId);
      }
      this.loading = false;
    } catch (error) {
      this.errorCodeMessage = error.data && error.data.message ? `errorcodes.${error.data.message}` : null;
      this.errorCode = (error.data && error.data.code) || 'BACKEND_ERROR';
    }
  },

  methods: {
    getName(name) {
      return name.replace('.', '_');
    },
    onClose() {
      this.$emit('close');
      this.$nextTick(() => {
        this.previousElement.focus();
      });
    },
    getVoicePartnerDescription() {
      const contentType = {};
      contentType.contentTypeCode = 'VoicePartners';
      contentType.contentTypeDescription = this.$t('barring.contentTypeDescription_VoicePartners');
      return contentType;
    },
  },
};
</script>

<style scoped>
.modal-title {
  padding-right: 40px;
  padding-left: 80px;
}

.modal-backdrop {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.3);
}

.modal-content {
  width: 85%;
  max-width: 820px;
  margin: auto;
}

.load-spin {
  padding-top: 50px;
  margin: auto;
}

.category {
  display: flex;
  flex-wrap: wrap;
  width: auto;
  min-width: 180px;
}

.description {
  display: flex;
  flex-wrap: wrap;
  padding-top: 5px;
  padding-right: 5px;
  padding-bottom: 5px;
  padding-left: 5px;
  margin-right: auto;
  margin-left: auto;
}

@media screen and (min-width: 720px) {
  div.description {
    min-width: 680px;
  }
}

@media screen and (max-width: 720px) {
  div.description {
    width: auto;
  }
}

dt {
  width: 25%;
  min-width: 80px;
  padding-top: 5px;
  padding-right: 5px;
  padding-bottom: 0;
  padding-left: 8px;
}

dl {
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  margin: 0;
}

dd {
  width: 75%;
  margin-left: auto;
}
</style>
